import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import {
  MagnifyingGlass, Bus, Wrench, Warning, BatteryCharging,
  CheckCircle, Trash, Plus, XCircle, Funnel,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Available": "#22C55E", "On Trip": "#1E3A8A", "Maintenance": "#F97316",
  "Breakdown": "#EF4444", "Charging": "#06B6D4", "Reserved": "#8B5CF6", "Scrapped": "#64748B",
};

const BS_TINTS = {
  BS2: "bg-slate-100 text-slate-700",
  BS3: "bg-orange-50 text-orange-700",
  BS4: "bg-amber-50 text-amber-700",
  BS6: "bg-emerald-50 text-emerald-700",
  EV:  "bg-blue-50 text-blue-700 font-bold",
};

const pickStr = (v) => (v === undefined || v === null || v === "") ? "—" : String(v);

export default function FleetInventory() {
  const [buses, setBuses] = useState([]);
  const depotMap = useDepots();
  const [opts, setOpts] = useState({
    categories: [], fuel_types: [], depots: [], manufacturers: [],
    bs_emissions: [], vehicle_classes: [], seat_types: [],
    fleet_categories: [], live_depots: [],
  });
  const [showAdd, setShowAdd] = useState(false);
  const [nb, setNb] = useState({
    fleet_id: "", registration_number: "", bus_type: "Non-AC",
    category: "Non-AC", fuel_type: "Diesel", manufacturer: "Tata Motors",
    depot: "Panaji", is_private: false,
  });

  // Filters
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [depot, setDepot] = useState("All");
  const [bsEmission, setBsEmission] = useState("All");
  const [vehicleClass, setVehicleClass] = useState("All");
  const [seatType, setSeatType] = useState("All");
  const [fleetCategory, setFleetCategory] = useState("All");
  const [serviceFlag, setServiceFlag] = useState("All");      // All | Active | Scrap

  // Server-side fetch — re-runs whenever any server filter changes.
  const fetchBuses = async () => {
    const p = new URLSearchParams();
    if (bsEmission !== "All")    p.set("bs_emission",    bsEmission);
    if (vehicleClass !== "All")  p.set("vehicle_class",  vehicleClass);
    if (seatType !== "All")      p.set("seat_type",      seatType);
    if (fleetCategory !== "All") p.set("fleet_category", fleetCategory);
    if (depot !== "All")         p.set("depot",          depot);
    if (serviceFlag === "Active") p.set("is_in_service", "true");
    if (serviceFlag === "Scrap")  p.set("is_in_service", "false");
    if (q.trim())                p.set("q",              q.trim());
    const { data } = await api.get(`/fleet/buses${p.toString() ? `?${p}` : ""}`);
    setBuses(data || []);
  };

  useEffect(() => {
    api.get("/fleet/options").then(({ data }) => setOpts(data));
  }, []);
  useEffect(() => {
    const t = setTimeout(fetchBuses, 200);
    return () => clearTimeout(t);
  }, [q, status, depot, bsEmission, vehicleClass, seatType, fleetCategory, serviceFlag]); // eslint-disable-line

  const addBus = async () => {
    if (!nb.fleet_id || !nb.registration_number) {
      toast.error("Fleet ID & Registration required");
      return;
    }
    try {
      const { data } = await api.post("/fleet/buses", nb);
      setBuses((p) => [data, ...p]);
      toast.success(`${data.fleet_id} added to fleet`);
      setShowAdd(false);
      setNb({ ...nb, fleet_id: "", registration_number: "" });
    } catch { toast.error("Failed to add bus"); }
  };

  // Client-side status filter (after server-side narrowing)
  const filtered = useMemo(() => buses.filter((b) =>
    status === "All" || b.status === status
  ), [buses, status]);

  const counts = useMemo(() => {
    const c = { total: buses.length, active: 0, scrap: 0, maintenance: 0, breakdown: 0, ev: 0 };
    for (const b of buses) {
      if (b.is_in_service === false) c.scrap++; else c.active++;
      if (b.status === "Maintenance") c.maintenance++;
      else if (b.status === "Breakdown") c.breakdown++;
      if ((b.bs_emission || "") === "EV") c.ev++;
    }
    return c;
  }, [buses]);

  const KPIS = [
    { k: "total",       l: "Showing",       v: counts.total,       c: "#1E3A8A", icon: Bus },
    { k: "active",      l: "In Service",    v: counts.active,      c: "#22C55E", icon: CheckCircle },
    { k: "scrap",       l: "Scrap/Out",     v: counts.scrap,       c: "#64748B", icon: XCircle },
    { k: "ev",          l: "EV Fleet",      v: counts.ev,          c: "#06B6D4", icon: BatteryCharging },
    { k: "maintenance", l: "Maintenance",   v: counts.maintenance, c: "#F97316", icon: Wrench },
    { k: "breakdown",   l: "Breakdown",     v: counts.breakdown,   c: "#EF4444", icon: Warning },
  ];

  const resetFilters = () => {
    setQ(""); setStatus("All"); setDepot("All"); setBsEmission("All");
    setVehicleClass("All"); setSeatType("All"); setFleetCategory("All");
    setServiceFlag("All");
  };

  const anyFilter = q || status !== "All" || depot !== "All" || bsEmission !== "All"
    || vehicleClass !== "All" || seatType !== "All" || fleetCategory !== "All"
    || serviceFlag !== "All";

  // Depot dropdown — union of live depots (from imported data) + legacy
  const depotOptions = useMemo(() => ["All",
    ...Array.from(new Set([...(opts.live_depots || []), ...(opts.depots || [])])).filter(Boolean),
  ], [opts]);

  const toggleService = async (b) => {
    const ns = b.status === "Maintenance" ? "Available" : "Maintenance";
    setBuses((p) => p.map((x) => x.id === b.id ? { ...x, status: ns } : x));
    try {
      await api.patch(`/fleet/buses/${b.id}/status`, { status: ns });
      toast.success(`${b.fleet_id || b.registration_number} → ${ns}`);
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Asset Registry</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Fleet Inventory</h1>
          <p className="text-[12px] text-slate-500 mt-1">
            Browse all buses imported from the official KTCL Fleet Chart —
            filter by BS emission · class · seat-type · service status.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 whitespace-nowrap"
                data-testid="fleet-add-btn">
          <Plus size={14}/> Add New Bus
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="fleet-kpis">
        {KPIS.map((k) => (
          <div key={k.k} className="ktcl-card ktcl-card-hover p-4">
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-500">{k.l}</div>
              <k.icon size={18} weight="duotone" style={{ color: k.c }}/>
            </div>
            <div className="font-display text-2xl font-bold mt-2 tabular-nums" style={{ color: k.c }}>
              {k.v.toLocaleString("en-IN")}
            </div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="ktcl-card">
        <div className="p-3 lg:p-4 flex flex-wrap items-center gap-2 lg:gap-3 border-b border-slate-200"
             data-testid="fleet-filters">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-md w-full sm:w-64">
            <MagnifyingGlass size={14} className="text-slate-500"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search registration / fleet / model…"
              data-testid="fleet-search"
              className="bg-transparent text-sm outline-none flex-1 min-w-0"/>
          </div>

          <Sel value={status} setter={setStatus} options={["All", ...Object.keys(STATUS_COLORS)]}
                label="Status" testid="fleet-filter-status"/>

          {/* Depot dropdown — values are codes, labels are full depot names. */}
          <label className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Depot</span>
            <select value={depot} onChange={(e) => setDepot(e.target.value)}
              data-testid="fleet-filter-depot"
              className="px-2.5 py-1.5 bg-slate-100 rounded-md text-[12px] outline-none border border-transparent focus:border-[#1E3A8A] min-w-[140px]">
              {depotOptions.map((d) => (
                <option key={d} value={d}>{d === "All" ? "All" : fmtDepot(d, depotMap)}</option>
              ))}
            </select>
          </label>

          <Sel value={bsEmission} setter={setBsEmission} options={["All", ...(opts.bs_emissions || [])]}
                label="BS Emission" testid="fleet-filter-bs"/>

          <Sel value={vehicleClass} setter={setVehicleClass} options={["All", ...(opts.vehicle_classes || [])]}
                label="Class" testid="fleet-filter-class"/>

          <Sel value={seatType} setter={setSeatType} options={["All", ...(opts.seat_types || [])]}
                label="Seat Type" testid="fleet-filter-seat"/>

          <Sel value={fleetCategory} setter={setFleetCategory} options={["All", ...(opts.fleet_categories || [])]}
                label="Category" testid="fleet-filter-category"/>

          <Sel value={serviceFlag} setter={setServiceFlag} options={["All", "Active", "Scrap"]}
                label="Service" testid="fleet-filter-service"/>

          {anyFilter && (
            <button onClick={resetFilters}
              data-testid="fleet-filter-reset"
              className="px-3 py-1.5 rounded-md text-[11.5px] font-bold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 inline-flex items-center gap-1">
              <XCircle size={12} weight="bold"/> Reset
            </button>
          )}

          <div className="ml-auto text-xs text-slate-500" data-testid="fleet-count-line">
            <Funnel size={11} weight="bold" className="inline mr-1"/>
            {filtered.length} of {buses.length}
          </div>
        </div>

        <div data-testid={ITMS.fleetTable}
             className="overflow-auto scroll-thin max-h-[calc(100vh-380px)]">
          {/* Desktop / tablet — classic table */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-bold">Reg / Fleet</th>
                <th className="px-3 py-2 font-bold">Make · Model</th>
                <th className="px-3 py-2 font-bold">BS</th>
                <th className="px-3 py-2 font-bold">Class</th>
                <th className="px-3 py-2 font-bold">Seat</th>
                <th className="px-3 py-2 font-bold text-right">Cap</th>
                <th className="px-3 py-2 font-bold">Depot</th>
                <th className="px-3 py-2 font-bold">Category</th>
                <th className="px-3 py-2 font-bold">Service</th>
                <th className="px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2 font-bold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const reg = b.registration_number || "—";
                const bs = b.bs_emission || "";
                return (
                  <tr key={b.id || reg} className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`fleet-row-${reg}`}>
                    <td className="px-3 py-2">
                      <div className="font-mono font-bold text-[#1E3A8A]">{reg}</div>
                      {b.fleet_id && <div className="text-[10.5px] text-slate-500 font-mono">{b.fleet_id}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{pickStr(b.make)}</div>
                      <div className="text-[10.5px] text-slate-500 truncate max-w-[200px]">{pickStr(b.model)}</div>
                    </td>
                    <td className="px-3 py-2">
                      {bs ? (
                        <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded font-mono ${BS_TINTS[bs] || "bg-slate-100 text-slate-700"}`}>
                          {bs}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{pickStr(b.vehicle_class)}</td>
                    <td className="px-3 py-2 text-[12px]">{pickStr(b.seat_type)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.capacity ?? "—"}</td>
                    <td className="px-3 py-2">{fmtDepot(b.depot, depotMap)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded
                                       ${(b.fleet_category === "Utility")
                                         ? "bg-purple-50 text-purple-700"
                                         : "bg-blue-50 text-[#1E3A8A]"}`}>
                        {b.fleet_category || "Bus"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold
                                       ${b.is_in_service === false ? "text-red-600" : "text-emerald-700"}`}>
                        <span className="w-1.5 h-1.5 rounded-full"
                          style={{ background: b.is_in_service === false ? "#EF4444" : "#22C55E" }}/>
                        {b.is_in_service === false ? "Scrap" : "Active"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full"
                              style={{ background: STATUS_COLORS[b.status] || "#94A3B8" }}/>
                        {b.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => toggleService(b)}
                        className="text-[11px] px-2 py-1 border border-slate-200 rounded hover:bg-slate-100 transition-colors">
                        {b.status === "Maintenance" ? "Mark Active" : "Service"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-12 text-center text-sm text-slate-500"
                          data-testid="fleet-empty">
                  No buses match these filters.
                  {anyFilter && <> <button onClick={resetFilters} className="underline font-bold text-[#1E3A8A]">Reset all</button></>}
                </td></tr>
              )}
            </tbody>
          </table>

          {/* Mobile — card list */}
          <div className="md:hidden divide-y divide-slate-100">
            {filtered.length === 0 && (
              <div data-testid="fleet-empty-mobile" className="px-3 py-12 text-center text-sm text-slate-500">
                No buses match these filters.
                {anyFilter && <> <button onClick={resetFilters} className="underline font-bold text-[#1E3A8A]">Reset all</button></>}
              </div>
            )}
            {filtered.map((b) => {
              const reg = b.registration_number || "—";
              const bs = b.bs_emission || "";
              return (
                <div key={b.id || reg} data-testid={`fleet-card-${reg}`}
                  className="px-3 py-3 flex items-start gap-3 hover:bg-slate-50 active:bg-slate-100">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-[#1E3A8A] text-[13px]">{reg}</span>
                      {b.fleet_id && <span className="text-[10px] text-slate-500 font-mono">· {b.fleet_id}</span>}
                      {bs && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded font-mono ${BS_TINTS[bs] || "bg-slate-100 text-slate-700"}`}>
                          {bs}
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] font-semibold text-slate-800 truncate">
                      {pickStr(b.make)} <span className="text-slate-500 font-normal">· {pickStr(b.model)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap text-[10.5px] text-slate-500">
                      <span>{fmtDepot(b.depot, depotMap)}</span>
                      <span>·</span>
                      <span>Cap {b.capacity ?? "—"}</span>
                      <span>·</span>
                      <span className={`inline-flex items-center gap-1 font-bold
                                       ${b.is_in_service === false ? "text-red-600" : "text-emerald-700"}`}>
                        <span className="w-1.5 h-1.5 rounded-full"
                          style={{ background: b.is_in_service === false ? "#EF4444" : "#22C55E" }}/>
                        {b.is_in_service === false ? "Scrap" : "Active"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded
                                       ${(b.fleet_category === "Utility")
                                         ? "bg-purple-50 text-purple-700"
                                         : "bg-blue-50 text-[#1E3A8A]"}`}>
                        {b.fleet_category || "Bus"}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-600 px-1.5 py-0.5 rounded bg-slate-50">
                        <span className="w-1.5 h-1.5 rounded-full"
                              style={{ background: STATUS_COLORS[b.status] || "#94A3B8" }}/>
                        {b.status || "—"}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => toggleService(b)}
                    data-testid={`fleet-service-btn-${reg}`}
                    className="flex-shrink-0 text-[11px] font-bold px-2.5 py-1.5 border border-slate-200 rounded hover:bg-slate-100 active:bg-slate-200 self-center">
                    {b.status === "Maintenance" ? "Active" : "Service"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">Add New Bus to Fleet</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400">✕</button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Fld label="Fleet ID *"><In v={nb.fleet_id} on={(v) => setNb({ ...nb, fleet_id: v })} ph="KT-1100"/></Fld>
              <Fld label="Registration No. *"><In v={nb.registration_number} on={(v) => setNb({ ...nb, registration_number: v })} ph="GA-07-F-1234"/></Fld>
              <Fld label="Bus Type / Category">
                <PlainSel v={nb.bus_type} on={(v) => setNb({ ...nb, bus_type: v, category: v })} options={opts.categories}/>
              </Fld>
              <Fld label="Fuel / Power Type">
                <PlainSel v={nb.fuel_type} on={(v) => setNb({ ...nb, fuel_type: v })} options={opts.fuel_types}/>
              </Fld>
              <Fld label="Manufacturer">
                <PlainSel v={nb.manufacturer} on={(v) => setNb({ ...nb, manufacturer: v })} options={opts.manufacturers}/>
              </Fld>
              <Fld label="Depot">
                <PlainSel v={nb.depot} on={(v) => setNb({ ...nb, depot: v })} options={opts.depots}/>
              </Fld>
              <Fld label="Ownership">
                <PlainSel v={nb.is_private ? "Private Operator" : "KTCL Owned"} on={(v) => setNb({ ...nb, is_private: v === "Private Operator" })} options={["KTCL Owned", "Private Operator"]}/>
              </Fld>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm">Cancel</button>
              <button onClick={addBus} className="ktcl-btn-primary px-5 py-2 rounded-md text-sm font-semibold">Add to Fleet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----- Helpers ------------------------------------------------------------
const Sel = ({ value, setter, options, label, testid }) => (
  <label className="flex items-center gap-1.5">
    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
    <select value={value} onChange={(e) => setter(e.target.value)}
      data-testid={testid}
      className="px-2.5 py-1.5 bg-slate-100 rounded-md text-[12px] outline-none border border-transparent focus:border-[#1E3A8A] min-w-[100px]">
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </label>
);
const Fld = ({ label, children }) => (<div><div className="text-xs font-medium text-slate-600 mb-1">{label}</div>{children}</div>);
const In = ({ v, on, ph = "" }) => (<input value={v} onChange={(e) => on(e.target.value)} placeholder={ph} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none"/>);
const PlainSel = ({ v, on, options }) => (<select value={v} onChange={(e) => on(e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm outline-none">{(options || []).map((o) => <option key={o}>{o}</option>)}</select>);
